import { useMutation } from "@apollo/client";
import {
  ADD_APPOINTMENT_V2,
} from "../../services/create-appointment-v2/create-appointment-v2.mutation";
import {
  IUpdatedCreateAppointmentResponse,
  IAppointmentCreateResponse,
} from "../../services/create-appointment-v2/create-appointment-v2.interface";
import {
  CANCEL_AND_ADD_APPOINTMENT,
} from "../../services/appointment/appointment.mutation";
import {
  BATCH_CANCEL_APPOINTMENTS,
} from "../../services/appointment/batch-cancel-appointment.mutation";
import {
  ICancelAndAddAppointmentRequest,
  IBatchCancelAppointmentResponse,
} from "../../services/appointment/appointment.interface";
import {
  GET_APPOINTMENTS,
} from "../../services/appointment-list/appointment-list.query";
import { DASHBOARD } from "../../services/dashboard/dashboard.query";
import { ASSIGNED_BOOKING_TYPE } from "../../config/params";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import { UrgentVisitStep } from "../../state/urgent-visit";
import {
  useUrgentVisitStore,
} from "../redux-hooks/urgent-visit-store/urgent-visit.store";
import { useAppointmentStore } from "../redux-hooks/appointment-store/appointment.store";
import { userActivityLogger } from "../../utils/mixpanel";

type PetReasonSource = { selectedReasons?: string[]; additionalNotes?: string };

const buildReasonsForVisit = (petData?: PetReasonSource): string => {
  const reasons = petData?.selectedReasons?.join(",") ?? "";
  const notes = petData?.additionalNotes ?? "";
  return notes ? `Description: ${notes},reasons: ${reasons}` : reasons;
};

export const useUrgentVisitBooking = () => {
  const {
    selectedPets, selectedSlot, selectedLocationId, selectedLocationDetails,
    petBookingData, promoCodes, setSlotUnavailableMessage,
    setBookingResponse, setStep, rescheduleAppointmentId, petAppointmentMap, timeZone,
    homeLocationId,
  } = useUrgentVisitStore();

  const isReschedule = !!rescheduleAppointmentId || Object.keys(petAppointmentMap).length > 0;

  const getBookingEventProps = () => ({
    appointment_category: "urgent",
    pet_name: selectedPets.map((p) => p.name).join(","),
    no_of_pets: selectedPets.length,
    date: selectedSlot?.time,
    location_name: selectedLocationDetails?.locationName,
    location_id: selectedLocationId,
    is_nearby_location: selectedLocationId !== homeLocationId,
    is_promo_used: promoCodes.length > 0,
    promocode: promoCodes.length > 0 ? promoCodes.join(", ") : undefined,
    is_reschedule: isReschedule,
  });

  type UrgentBookingFailureReason =
    | "slot_unavailable"
    | "reschedule_failed"
    | "batch_cancel_failed"
    | "network_error"
    | "exception";

  const logUrgentBookingFailure = (
    failureReason: UrgentBookingFailureReason,
    error?: string,
  ) => {
    userActivityLogger(MIXPANEL_EVENTS.URGENT_VISIT_BOOKING_FAILURE, {
      ...getBookingEventProps(),
      failure_reason: failureReason,
      error,
    });
  };

  const { appointmentDetails } = useAppointmentStore();

  const [addAppointment, { loading: addLoading }] = useMutation<{
    addAppointmentV2: IUpdatedCreateAppointmentResponse;
  }>(ADD_APPOINTMENT_V2, {
    refetchQueries: [
      { query: GET_APPOINTMENTS },
      { query: DASHBOARD },
    ],
    onCompleted: (data) => {
      if (!data?.addAppointmentV2) {
        logUrgentBookingFailure("exception", "empty_response");
        setSlotUnavailableMessage(
          "Something went wrong while booking your appointment. Please try again.",
        );
        return;
      }

      if (data.addAppointmentV2.slotUnavailable) {
        logUrgentBookingFailure("slot_unavailable");
        setSlotUnavailableMessage(
          "This time slot is no longer available. No worries," +
          " select a new time slot to confirm the appointment booking.",
        );

        return;
      }

      if (data.addAppointmentV2.success === false) {
        logUrgentBookingFailure(
          "exception",
          data.addAppointmentV2.messages?.[0],
        );
        setSlotUnavailableMessage(
          data.addAppointmentV2.messages?.[0] ||
          "Something went wrong while booking your appointment. Please try again.",
        );
        return;
      }

      setBookingResponse({
        start_at: data.addAppointmentV2.appointments[0]?.start_at,
        end_at: data.addAppointmentV2.appointments[0]?.end_at,
        duration: data.addAppointmentV2.appointments[0]?.duration,
        appointmentCreateType:
          data.addAppointmentV2.appointments[0]?.appointmentCreateType,
        promoCode:
          data.addAppointmentV2.appointments[0]?.promoCode,
        resources:
          data.addAppointmentV2.appointments[0]?.resources || [],
        pets: data.addAppointmentV2.appointments.map((a) => ({
          id: a.pet.id,
          name: a.pet.name,
          image: a.pet.image,
          speciesName: a.pet.speciesName,
          breedName: a.pet.breedName,
          reasonForVisit: a.pet.reasonForVisit,
        })),
        location: selectedLocationDetails ?
          {
            idLocation: selectedLocationDetails.idLocation,
            locationName: selectedLocationDetails.locationName,
            address: selectedLocationDetails.address,
            city: selectedLocationDetails.city,
            position: selectedLocationDetails.position,
            email: selectedLocationDetails.email,
            phoneNumber: selectedLocationDetails.phoneNumber,
            timeZone: selectedLocationDetails.timeZone || timeZone,
          } :
          data.addAppointmentV2.location,
        success: data.addAppointmentV2.success,
        messages: data.addAppointmentV2.messages,
      });

      userActivityLogger(MIXPANEL_EVENTS.URGENT_VISIT_BOOKING_SUCCESS, getBookingEventProps());
      setStep(UrgentVisitStep.BOOKED);
    },
    onError: (error) => {
      logUrgentBookingFailure("network_error", error?.message);
      setSlotUnavailableMessage(
        "Something went wrong while booking your appointment. Please try again.",
      );
    },
  });

  const [cancelAndAddAppointment, { loading: rescheduleLoading }] = useMutation<{
    cancelAndAddAppointment: IAppointmentCreateResponse;
  }>(CANCEL_AND_ADD_APPOINTMENT, {
    refetchQueries: [
      { query: GET_APPOINTMENTS },
      { query: DASHBOARD },
    ],
    onCompleted: (data) => {
      if (!data?.cancelAndAddAppointment) {
        logUrgentBookingFailure("exception", "empty_response");
        setSlotUnavailableMessage(
          "Something went wrong while rescheduling your appointment. Please try again.",
        );
        return;
      }

      if (data.cancelAndAddAppointment.success === false) {
        logUrgentBookingFailure(
          "reschedule_failed",
          data.cancelAndAddAppointment.messages?.[0],
        );
        setSlotUnavailableMessage(
          "This time slot is no longer available. No worries," +
          " select a new time slot to confirm the appointment booking.",
        );

        return;
      }

      setBookingResponse({
        start_at: data.cancelAndAddAppointment.start_at,
        end_at: data.cancelAndAddAppointment.end_at,
        duration: data.cancelAndAddAppointment.duration,
        appointmentCreateType: data.cancelAndAddAppointment.appointmentCreateType,
        promoCode: data.cancelAndAddAppointment.promoCode,
        resources: data.cancelAndAddAppointment.resources || [],
        pets: (data.cancelAndAddAppointment.pets || []).map((pet) => {
          // Prefer the locally edited reason so the confirmation reflects the
          // user's changes even before the backend persists them.
          const localReasonForVisit = buildReasonsForVisit(petBookingData[pet.id]);
          return {
            id: pet.id,
            name: pet.name,
            image: pet.image,
            speciesName: pet.speciesName,
            breedName: pet.breedName,
            reasonForVisit: localReasonForVisit || pet.reasonForVisit,
          };
        }),
        location: selectedLocationDetails ?
          {
            idLocation: selectedLocationDetails.idLocation,
            locationName: selectedLocationDetails.locationName,
            address: selectedLocationDetails.address,
            city: selectedLocationDetails.city,
            position: selectedLocationDetails.position,
            email: selectedLocationDetails.email,
            phoneNumber: selectedLocationDetails.phoneNumber,
            timeZone: selectedLocationDetails.timeZone || timeZone,
          } :
          data.cancelAndAddAppointment.location,
        success: data.cancelAndAddAppointment.success,
        messages: data.cancelAndAddAppointment.messages,
      });

      userActivityLogger(MIXPANEL_EVENTS.URGENT_VISIT_BOOKING_SUCCESS, getBookingEventProps());
      setStep(UrgentVisitStep.BOOKED);
    },
    onError: (error) => {
      logUrgentBookingFailure("network_error", error?.message);
      setSlotUnavailableMessage(
        "Something went wrong while rescheduling your appointment. Please try again.",
      );
    },
  });

  const [batchCancel, { loading: batchCancelLoading }] = useMutation<{
    batchCancelAppointments: IBatchCancelAppointmentResponse;
  }>(BATCH_CANCEL_APPOINTMENTS, {
    refetchQueries: [
      { query: GET_APPOINTMENTS },
      { query: DASHBOARD },
    ],
  });

  const createUrgentBooking = async () => {
    if (!selectedSlot || !selectedLocationId) return;

    const petMapKeys = Object.keys(petAppointmentMap);
    const isGroupedReschedule = petMapKeys.length > 0;

    if (isGroupedReschedule && selectedPets.length === 1) {
      // Single pet from a group: use atomic cancelAndAddAppointment
      const pet = selectedPets[0];
      const appointmentId = petAppointmentMap[pet.id];

      if (!appointmentId) {
        setSlotUnavailableMessage(
          "Something went wrong identifying the appointment. Please try again.",
        );
        return;
      }

      const appointmentRequest: ICancelAndAddAppointmentRequest = {
        appointmentFrom: "Web",
        appointmentId,
        appointmentType: appointmentDetails?.appointmentTypeId!,
        locationId: selectedLocationId,
        petId: pet.id,
        sales_resource: +selectedSlot.resourcesId,
        start_at: +selectedSlot.time,
        appointmentCreateType: ASSIGNED_BOOKING_TYPE.URGENT_BOOKING,
        description: buildReasonsForVisit(petBookingData[pet.id]),
      };

      cancelAndAddAppointment({
        variables: { data: appointmentRequest },
      });
    } else if (isGroupedReschedule && selectedPets.length > 1) {
      // Multiple pets from a group: batch cancel then create new
      const appointmentIds = selectedPets
        .map((pet) => petAppointmentMap[pet.id])
        .filter(Boolean);

      try {
        const { data: cancelData } = await batchCancel({
          variables: {
            data: { appointmentIds },
          },
        });

        if (!cancelData?.batchCancelAppointments?.success) {
          logUrgentBookingFailure(
            "batch_cancel_failed",
            cancelData?.batchCancelAppointments?.messages?.[0],
          );
          setSlotUnavailableMessage(
            cancelData?.batchCancelAppointments?.messages?.[0] ||
            "Failed to cancel existing appointments. Please try again.",
          );
          return;
        }

        const pets = selectedPets.map((pet) => ({
          petId: pet.id,
          reasonsForVisit: buildReasonsForVisit(petBookingData[pet.id]),
          promoCode: promoCodes.length ? promoCodes : undefined,
        }));

        addAppointment({
          variables: {
            data: {
              pets,
              start_at: selectedSlot.time,
              sales_resource: +selectedSlot.resourcesId,
              locationId: selectedLocationId,
              appointmentFrom: "Web",
              appointmentCreateType: ASSIGNED_BOOKING_TYPE.URGENT_BOOKING,
            },
          },
        });
      } catch (err) {
        logUrgentBookingFailure("exception", err instanceof Error ? err.message : String(err));
        setSlotUnavailableMessage(
          "Something went wrong while rescheduling your appointments. Please try again.",
        );
      }
    } else if (rescheduleAppointmentId) {
      // Single pet reschedule (not from group)
      const appointmentRequest: ICancelAndAddAppointmentRequest = {
        appointmentFrom: "Web",
        appointmentId: rescheduleAppointmentId,
        appointmentType: appointmentDetails?.appointmentTypeId!,
        locationId: selectedLocationId,
        petId: selectedPets[0]?.id,
        sales_resource: +selectedSlot.resourcesId,
        start_at: +selectedSlot.time,
        appointmentCreateType: ASSIGNED_BOOKING_TYPE.URGENT_BOOKING,
        description: buildReasonsForVisit(petBookingData[selectedPets[0]?.id ?? ""]),
      };

      cancelAndAddAppointment({
        variables: { data: appointmentRequest },
      });
    } else {
      const pets = selectedPets.map((pet) => ({
        petId: pet.id,
        reasonsForVisit: buildReasonsForVisit(petBookingData[pet.id]),
        promoCode: promoCodes.length ? promoCodes : undefined,
      }));

      addAppointment({
        variables: {
          data: {
            pets,
            start_at: selectedSlot.time,
            sales_resource: +selectedSlot.resourcesId,
            locationId: selectedLocationId,
            appointmentFrom: "Web",
            appointmentCreateType: ASSIGNED_BOOKING_TYPE.URGENT_BOOKING,
          },
        },
      });
    }
  };

  return {
    createUrgentBooking,
    loading: addLoading || rescheduleLoading || batchCancelLoading,
  };
};
